// Import SortableJS
import Sortable from 'sortablejs';

// === Image Compression Function ===
async function compressImage(file) {
  const maxSizeInBytes = 1 * 1024 * 1024; // 1MB
  const maxWidth = 1200;
  const maxHeight = 1200;
  const quality = 0.9;

  if (file.size <= maxSizeInBytes) return file;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = function (e) {
      const img = new Image();
      img.src = e.target.result;

      img.onload = function () {
        let width = img.width;
        let height = img.height;

        if (width > height && width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        } else if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              })
            );
          },
          "image/jpeg",
          quality
        );
      };
    };
  });
}

// === Helper: Sync fileInput.files with selectedFiles ===
function syncFileInput(fileInput, selectedFiles) {
  const dataTransfer = new DataTransfer();
  selectedFiles.forEach((file) => dataTransfer.items.add(file));
  fileInput.files = dataTransfer.files;
}

// === Render Preview Function with fixed order ===
function renderPreview(files, container, fileInput) {
  container.innerHTML = "";

  const promises = files.map((file, index) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const preview = document.createElement("div");
      preview.className = "preview-item";
      preview.dataset.index = index;
      preview.dataset.uid = file._uid;
      preview.style.position = "relative";
      preview.style.display = "inline-block";
      preview.style.margin = "5px";

      preview.innerHTML = `
        <img src="${e.target.result}" style="max-width:100px; max-height:100px; border:1px solid #ccc; border-radius:6px;">
        <div class="progress" style="height:5px;">
          <div class="progress-bar" role="progressbar" style="width:0%"></div>
        </div>
      `;

      const removeBtn = document.createElement("button");
      removeBtn.innerHTML = "<i class='fas fa-times'></i>";
      removeBtn.style.position = "absolute";
      removeBtn.style.top = "2px";
      removeBtn.style.right = "2px";
      removeBtn.style.background = "rgba(0,0,0,0.6)";
      removeBtn.style.color = "#fff";
      removeBtn.style.border = "none";
      removeBtn.style.borderRadius = "50%";
      removeBtn.style.width = "20px";
      removeBtn.style.height = "20px";
      removeBtn.style.cursor = "pointer";
      removeBtn.style.lineHeight = "18px";
      removeBtn.style.fontSize = "12px";

      removeBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        const removeIndex = files.findIndex(f => f._uid === file._uid);
        if (removeIndex > -1) {
          files.splice(removeIndex, 1);
          syncFileInput(fileInput, files);
          preview.remove();
        }
      });
      removeBtn.addEventListener("mousedown", (ev) => ev.stopPropagation());
      preview.appendChild(removeBtn);

      resolve({ index, preview });
    };
    reader.readAsDataURL(file);
  }));

  Promise.all(promises).then((results) => {
    // Ensure order matches files array
    results.sort((a, b) => a.index - b.index).forEach(r => container.appendChild(r.preview));
  });
}

// === Drag Sort Function ===
let sortableInstance = null;

function enableDragSort(container, selectedFiles, fileInput) {
  if (sortableInstance) sortableInstance.destroy();

  sortableInstance = new Sortable(container, {
    animation: 200,
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    fallbackOnBody: true,
    swap: true,
    swapThreshold: 0.65,
    filter: 'button',
    preventOnFilter: false,

    onEnd: function(evt) {
      const oldIndex = evt.oldIndex;
      const newIndex = evt.newIndex;

      if (oldIndex === newIndex || oldIndex === undefined || newIndex === undefined) return;

      const [moved] = selectedFiles.splice(oldIndex, 1);
      selectedFiles.splice(newIndex, 0, moved);

      syncFileInput(fileInput, selectedFiles);
      console.log("Updated sequence after drag & drop:", selectedFiles.map(f => f.name));
    }
  });
}

// === Cloudinary Upload Handler ===
function initializeCloudinaryUpload() {
  const form = document.getElementById("cloudinary-upload-form");
  if (!form) return;

  const fileInput = form.querySelector('input[type="file"]');
  const uploadLabel = form.querySelector(".file-upload-label span");
  const uploadSmall = form.querySelector(".file-upload-label small");
  const uploadStatus = document.getElementById("upload-status");
  const uploadPreview = document.querySelector(".upload-preview");
  const submitButton = form.querySelector('button[type="submit"]');

  let selectedFiles = [];

  fileInput.addEventListener("change", (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => { file._uid = Date.now() + Math.random(); });

    selectedFiles = [...selectedFiles, ...files];

    syncFileInput(fileInput, selectedFiles);
    uploadLabel.textContent = `${selectedFiles.length} ${selectedFiles.length === 1 ? "file" : "files"} selected`;
    uploadSmall.textContent = "Click to change";

    renderPreview(selectedFiles, uploadPreview, fileInput);
    enableDragSort(uploadPreview, selectedFiles, fileInput);
  });

  form.addEventListener("submit", async function (e) {
    e.preventDefault();
    if (!selectedFiles.length) return toastr.error("Please select at least one image");

    submitButton.disabled = true;
    uploadStatus.style.display = "block";
    uploadStatus.className = "alert alert-info";
    uploadStatus.textContent = "Compressing and uploading images...";

    try {
      const compressedFiles = await Promise.all(selectedFiles.map(f => compressImage(f)));
      const uploadedFiles = [];

      for (let i = 0; i < compressedFiles.length; i++) {
        const file = compressedFiles[i];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "direct_upload_preset");
        formData.append("folder", "property_images");

        uploadStatus.textContent = `Uploading image ${i+1} of ${compressedFiles.length}...`;

        const response = await fetch(form.action, { method: "POST", body: formData });
        const data = await response.json();
        if (!response.ok || data.error) throw new Error(data.error?.message || "Upload failed");

        // Keep original Cloudinary response, add sequence only
        data.sequence = i + 1;
        uploadedFiles.push(data);

        const progressBar = uploadPreview.children[i].querySelector(".progress-bar");
        progressBar.style.width = "100%";
        progressBar.style.backgroundColor = "#28a745";
      }

      // Send to Rails
      const railsFormData = new FormData();
      uploadedFiles.forEach(fileData => railsFormData.append("property[cloudinary_ids][]", JSON.stringify(fileData)));
      railsFormData.append("authenticity_token", form.querySelector('[name="authenticity_token"]').value);

      const railsResponse = await fetch(form.dataset.railsUrl, {
        method: "POST",
        body: railsFormData,
        headers: { "X-CSRF-Token": document.querySelector("[name='csrf-token']").content, Accept: "application/json" }
      });

      if (railsResponse.ok) {
        toastr.info("Your images have been queued. Please refresh the page shortly.");
        setTimeout(() => window.location.reload(), 5000);
      } else throw new Error("Failed to save images to Rails");
    } catch (error) {
      uploadStatus.className = "alert alert-danger";
      uploadStatus.textContent = `Upload failed: ${error.message}`;
      toastr.error("Error uploading images. Please try again.");
      submitButton.disabled = false;
    }
  });
}

document.addEventListener("turbolinks:load", initializeCloudinaryUpload);
